import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BarekamText from "../../../../../pages/components/text";
import Message from "../../../../../pages/components/message";
import { MessageSections } from "../../../../../pages/components/message/message";
import {
  SettingsContainer,
  SettingsTabContainer,
} from "../../../../../pages/user/settings/styled";
import { WarehouseModel } from "../../../../../pages/shared/models/order/warehouse.model";
import { ActionBarWithSaveButton } from "../../../../../pages/user/settings/user-actionbar/action-bar-with-save-button";
import BarekamInput from "../../../../../pages/components/input";
import { useAppSelector } from "../../../../../redux/store";
import BarekamPhoneInput from "../../../../../pages/auth/phone-input/phone-input";
import { AxiosError } from "axios";
import warehousesService, {
  WarehouseUpdateMeta,
} from "../../../../../services/warehouses.service";
import { getMessage, initToast } from "../../../../../pages/shared/helper-functions";
import { ToastContainer } from "react-toastify";

export const WarehouseInfo = ({ warehouse }: { warehouse: WarehouseModel }) => {
  const [isModified, setIsModified] = useState(false);

  const [modifiedWarehouse, setModifiedWarehouse] = useState<WarehouseModel>(
    {} as WarehouseModel,
  );
  const [shippingPriceByLandError, setShippingPriceByLandError] =
    useState<string>();
  const [shippingPriceByAirError, setShippingPriceByAirError] =
    useState<string>();

  const errors = {} as { [key: string]: string };

  const navigate = useNavigate();

  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );

  function saveWarehouse() {
    return warehousesService
      .editWarehouse(warehouse.warehouseId, modifiedWarehouse)
      .then((response: WarehouseUpdateMeta) => {
        if (response.acknowledged) {
          const toastMessage = getMessage("saved", MessageSections.USER, messageCache, languageId);
          const tooltipType = "success";
          initToast(toastMessage, tooltipType);
          setIsModified(false);
          navigate(-1);
        }
      })
      .catch((e) => {
        if (e instanceof AxiosError) {
          return e.response?.data;
        }
      });
  }

  function onChange(value: any, field: string) {
    setIsModified(true);

    setModifiedWarehouse((prevState) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      prevState[field] = value;

      return { ...prevState };
    });
  }

  const PrimaryActionText = () => (
    <BarekamText variant={"m"} bold>
      <Message messageKey={"save"} sectionId={MessageSections.USER} />
    </BarekamText>
  );

  return (
    <SettingsContainer>
      <ActionBarWithSaveButton
        disabled={!!warehouse}
        headerText={<div />}
        primaryActionText={isModified ? <PrimaryActionText /> : undefined}
        onPrimaryActionClick={saveWarehouse}
      />
      <SettingsTabContainer>
        <BarekamInput
          key={"name"}
          customInputVariant={"standard"}
          placeholder={getMessage(
            "name",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          value={
            (modifiedWarehouse && modifiedWarehouse.name) || warehouse.name
          }
          onChange={(event) => onChange(event.target.value, "name")}
          error={!!errors["name"]}
          errorMessage={
            errors["name"] &&
            getMessage(
              errors["name"],
              MessageSections.MAIN,
              messageCache,
              languageId,
            )
          }
          width={"50%"}
        />

        <BarekamInput
          key={"city"}
          customInputVariant={"standard"}
          placeholder={getMessage(
            "city",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          value={
            (modifiedWarehouse && modifiedWarehouse.city) || warehouse.city
          }
          onChange={(event) => onChange(event.target.value, "city")}
          error={!!errors["city"]}
          errorMessage={
            errors["city"] &&
            getMessage(
              errors["city"],
              MessageSections.MAIN,
              messageCache,
              languageId,
            )
          }
          width={"50%"}
        />

        <BarekamInput
          key={"state"}
          customInputVariant={"standard"}
          placeholder={getMessage(
            "state",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          value={
            (modifiedWarehouse && modifiedWarehouse.state) || warehouse.state
          }
          onChange={(event) => onChange(event.target.value, "state")}
          error={!!errors["state"]}
          errorMessage={
            errors["state"] &&
            getMessage(
              errors["state"],
              MessageSections.MAIN,
              messageCache,
              languageId,
            )
          }
          width={"50%"}
        />

        <BarekamInput
          key={"addressLine1"}
          customInputVariant={"standard"}
          placeholder={getMessage(
            "addressLine1",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          value={
            (modifiedWarehouse && modifiedWarehouse.addressLine1) ||
            warehouse.addressLine1
          }
          onChange={(event) => onChange(event.target.value, "addressLine1")}
          error={!!errors["addressLine1"]}
          errorMessage={
            errors["addressLine1"] &&
            getMessage(
              errors["addressLine1"],
              MessageSections.MAIN,
              messageCache,
              languageId,
            )
          }
          width={"50%"}
        />

        <BarekamInput
          key={"zipCode"}
          customInputVariant={"standard"}
          placeholder={getMessage(
            "zipCode",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          value={
            (modifiedWarehouse && modifiedWarehouse.zipCode) ||
            warehouse.zipCode
          }
          onChange={(event) => onChange(event.target.value, "zipCode")}
          error={!!errors["zipCode"]}
          errorMessage={
            errors["zipCode"] &&
            getMessage(
              errors["zipCode"],
              MessageSections.MAIN,
              messageCache,
              languageId,
            )
          }
          width={"50%"}
        />

        <BarekamPhoneInput
          key={"register-phone-number"}
          value={
            (modifiedWarehouse && modifiedWarehouse.phone) || warehouse.phone
          }
          placeholder={getMessage(
            "phone_number",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          onChange={(value: number) => onChange(value, "phone")}
          error={!!errors["phone"]}
          errorMessage={
            errors["phone"] &&
            getMessage(
              errors["phone"],
              MessageSections.MAIN,
              messageCache,
              languageId,
            )
          }
          width={"50%"}
          formatted
        />

        <BarekamInput
          value={
            (modifiedWarehouse && modifiedWarehouse.shippingPriceByLand) ||
            warehouse.shippingPriceByLand
          }
          placeholder={getMessage(
            "shipping_price_by_land_amd",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          label={getMessage(
            "shipping_price_by_land_amd",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          type={"number"}
          customInputVariant={"standard"}
          width={"50%"}
          onChange={(event) =>
            onChange(event.target.value, "shippingPriceByLand")
          }
          error={!!shippingPriceByLandError}
          errorMessage={
            shippingPriceByLandError &&
            getMessage(
              shippingPriceByLandError,
              MessageSections.MAIN,
              messageCache,
              languageId,
            )
          }
        />

        <BarekamInput
          value={
            (modifiedWarehouse && modifiedWarehouse.shippingPriceByAir) ||
            warehouse.shippingPriceByAir
          }
          placeholder={getMessage(
            "shipping_price_by_air_amd",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          label={getMessage(
            "shipping_price_by_air_amd",
            MessageSections.MAIN,
            messageCache,
            languageId,
          )}
          type={"number"}
          customInputVariant={"standard"}
          width={"50%"}
          onChange={(event) =>
            onChange(event.target.value, "shippingPriceByAir")
          }
          error={!!shippingPriceByAirError}
          errorMessage={
            shippingPriceByAirError &&
            getMessage(
              shippingPriceByAirError,
              MessageSections.MAIN,
              messageCache,
              languageId,
            )
          }
        />
      </SettingsTabContainer>
      <ToastContainer />
    </SettingsContainer>
  );
};
